/**
 * Route guard for the call-centre Daily report
 * (`/dashboard/callcenter/daily-report`) -- MANAGER-ROLLUP-REACHABILITY-V1.
 * Mirrors RequireManagerTracking: the pure decision lives in
 * decideCallCenterDailyReportAccess (callcenter-daily-report-access.ts) and
 * keys off the caller's role via the combined
 * CALLCENTER_DAILY_REPORT_ROUTE_AUDIENCE_ROLES set (call_center_agent, owner,
 * gm_agm, regional_head). Any other role redirects to its own homePath.
 *
 * This REPLACES RequireNavSection section="call_center" on this one route so
 * the leadership tier is admitted to the manager rollup
 * (AGENT-DAY-PRODUCTIVITY-ROLLUP-V1) by hierarchy read-down WITHOUT inheriting
 * the call_center nav section (rule 3, decoupled from me.navSections).
 * call_center_agent behaviour is byte-identical (it rendered before, it
 * renders now); only owner, gm_agm, and regional_head are newly admitted.
 *
 * SEE-NOT-DO (doctrine rule 2): a READ gate only. Backend route auth
 * (getCallcenterDailyReport's own audience check) remains the real security
 * boundary; this guard is UX cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideCallCenterDailyReportAccess } from '@/dashboard/callcenter/daily-report/callcenter-daily-report-access';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireCallCenterDailyReportViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireCallCenterDailyReportView({
  loading,
  error,
  me,
  children,
}: RequireCallCenterDailyReportViewProps): JSX.Element | null {
  const decision = decideCallCenterDailyReportAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireCallCenterDailyReport(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireCallCenterDailyReportView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
